import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { 
  AlertTriangle, 
  AlertCircle, 
  CheckCircle, 
  Clock, 
  TrendingUp,
  TrendingDown,
  RefreshCw
} from 'lucide-react'
import { api } from '@/lib/api'

interface TimeRange {
  label: string
  value: string
  days: number
}

interface AlertData {
  id: string
  type: 'error' | 'warning' | 'info'
  title: string
  message: string
  server_name: string
  timestamp: string
  resolved: boolean
  severity: 'high' | 'medium' | 'low'
}

interface AlertSummaryProps {
  timeRange: TimeRange
}

export function AlertSummary({ timeRange }: AlertSummaryProps) {
  const [alerts, setAlerts] = useState<AlertData[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'error' | 'warning' | 'info'>('all')

  useEffect(() => {
    fetchAlerts()
  }, [timeRange])

  const fetchAlerts = async () => {
    try {
      setLoading(true)
      // Get recent alerts (active by default), then map to display model
      const res = await api.get<any>('/alerts', { status: 'active', limit: 100 })
      const items: AlertData[] = (res?.alerts || []).map((a: any) => ({
        id: String(a.id || `${a.server_id}-${a.type}-${a.timestamp}`),
        type: a.severity === 'critical' ? 'error' : a.severity === 'warning' ? 'warning' : 'info',
        title: a.type?.toString().replace(/_/g, ' ') || 'Alert',
        message: a.message || '',
        server_name: a.server_id || 'Unknown',
        timestamp: typeof a.timestamp === 'string' ? a.timestamp : new Date().toISOString(),
        resolved: a.status === 'resolved',
        severity: a.severity === 'critical' ? 'high' : a.severity === 'warning' ? 'medium' : 'low',
      }))

      setAlerts(items)
    } catch (error) {
      console.error('Failed to fetch alerts:', error)
    } finally {
      setLoading(false)
    }
  }

  const getFilteredAlerts = () => {
    if (filter === 'all') return alerts
    return alerts.filter(alert => alert.type === filter)
  }

  const getAlertCounts = () => {
    const total = alerts.length
    const errors = alerts.filter(a => a.type === 'error').length
    const warnings = alerts.filter(a => a.type === 'warning').length
    const info = alerts.filter(a => a.type === 'info').length
    const unresolved = alerts.filter(a => !a.resolved).length
    
    return { total, errors, warnings, info, unresolved }
  }

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-red-500" />
      case 'warning':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />
      case 'info':
        return <CheckCircle className="h-4 w-4 text-blue-500" />
      default:
        return <AlertTriangle className="h-4 w-4 text-gray-500" />
    }
  }

  const getAlertBadgeVariant = (type: string) => {
    switch (type) {
      case 'error':
        return 'destructive'
      case 'warning':
        return 'outline'
      case 'info':
        return 'secondary'
      default:
        return 'outline'
    }
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))
    
    if (diffHours < 1) return 'Just now'
    if (diffHours < 24) return `${diffHours}h ago`
    return date.toLocaleDateString()
  }

  const counts = getAlertCounts()
  const filteredAlerts = getFilteredAlerts()

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Alert Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-500 mr-2" />
            <span className="text-muted-foreground">Loading alerts...</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      {/* Alert Statistics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5" />
            <span>Alert Statistics</span>
          </CardTitle>
          <CardDescription>Overview for {timeRange.label.toLowerCase()}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-red-50 rounded-lg">
                <div className="flex items-center space-x-2 mb-1">
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                  <span className="text-sm font-medium">Errors</span>
                </div>
                <div className="text-2xl font-bold text-red-600">{counts.errors}</div>
              </div>
              
              <div className="p-3 bg-yellow-50 rounded-lg">
                <div className="flex items-center space-x-2 mb-1">
                  <AlertCircle className="h-4 w-4 text-yellow-500" />
                  <span className="text-sm font-medium">Warnings</span>
                </div>
                <div className="text-2xl font-bold text-yellow-600">{counts.warnings}</div>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Alerts</span>
                <span className="font-medium">{counts.total}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Unresolved</span>
                <span className="font-medium text-red-600">{counts.unresolved}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Resolution Rate</span>
                <span className="font-medium text-green-600">
                  {counts.total > 0 ? Math.round(((counts.total - counts.unresolved) / counts.total) * 100) : 0}%
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Alerts */}
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Clock className="h-5 w-5" />
            <span>Recent Alerts</span>
          </CardTitle>
          <CardDescription>Latest alerts and their status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Filter Buttons */}
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'all', label: 'All', count: counts.total },
                { key: 'error', label: 'Errors', count: counts.errors },
                { key: 'warning', label: 'Warnings', count: counts.warnings },
                { key: 'info', label: 'Info', count: counts.info }
              ].map(({ key, label, count }) => (
                <Button
                  key={key}
                  variant={filter === key ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilter(key as any)}
                  className="flex items-center space-x-1"
                >
                  <span>{label}</span>
                  <Badge variant="secondary" className="ml-1 text-xs">
                    {count}
                  </Badge>
                </Button>
              ))}
            </div>

            {/* Alert List */}
            <div className="space-y-3">
              {filteredAlerts.slice(0, 10).map((alert) => (
                <div key={alert.id} className="flex items-start space-x-3 p-3 border rounded-lg">
                  <div className="flex-shrink-0 mt-1">
                    {getAlertIcon(alert.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-medium text-sm">{alert.title}</h4>
                      <Badge variant={getAlertBadgeVariant(alert.type)} className="text-xs">
                        {alert.type}
                      </Badge>
                      {alert.resolved && (
                        <Badge variant="outline" className="text-xs text-green-600">
                          Resolved
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{alert.message}</p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{alert.server_name}</span>
                      <span>{formatTimestamp(alert.timestamp)}</span>
                    </div>
                  </div>
                </div>
              ))}
              
              {filteredAlerts.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No {filter === 'all' ? '' : filter} alerts found
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
} 